import moment from 'moment-timezone';

import {
	type CreateWorkerData,
	MobileAppStatus,
	WorkerCandidateStatus,
	type WorkerDetail,
	type WorkerJob,
	WorkerType,
} from '@/modules/worker/types';

import type { IJobStored } from '@/types/job';

const COMMAND_WORKER_FIELDS = [
	'firstname',
	'lastname',
	'email',
	'phone',
	'workerType',
	'candidateStatus',
	'mobileAppStatus',
	'origin',
	'originOfApplication',
	'besoins',
	'agencies',
	'cv',
	'createdAt',
	'updatedAt',
	'birthDate',
	'delegable',
	'evoliaId',
] as const;

const buildJobsFromPayload = (
	payload: CreateWorkerData,
	jobsFromForm: IJobStored[]
): WorkerJob[] => {
	if (jobsFromForm.length > 0) {
		return jobsFromForm.map((job) => ({
			jobId: job.value,
			jobName: job.label || null,
			experience: null,
			degreeLevel: null,
		}));
	}

	return payload.jobs.map((jobId) => ({
		jobId,
		jobName: null,
		experience: null,
		degreeLevel: null,
	}));
};

export const buildOptimisticWorkerDetail = (
	payload: CreateWorkerData,
	commandResponse: Record<string, unknown>,
	jobsFromForm: IJobStored[] = []
): WorkerDetail => {
	const now = Date.now();
	const id = String(commandResponse.id ?? '');
	const cvPath = payload.cv?.path?.trim();
	const cv = cvPath ? { path: cvPath } : null;

	const worker: WorkerDetail = {
		id,
		evoliaId: null,
		delegable: true,
		firstname: payload.firstname,
		lastname: payload.lastname,
		email: payload.email,
		phone: payload.phone,
		cv,
		workerType: WorkerType.CANDIDATE,
		mobileAppStatus: MobileAppStatus.NEW,
		candidateStatus: WorkerCandidateStatus.NEW,
		companies: { refused: [], approved: [] },
		jobs: buildJobsFromPayload(payload, jobsFromForm),
		agencies: [],
		regularAvailabilities: [],
		nightShift: false,
		weekendShift: false,
		vehicle: false,
		carpooling: false,
		createdAt: now,
		updatedAt: now,
		origin: payload.source,
		askedChanges: null,
		anonymizedCv: null,
	};

	if (payload.birthDate) {
		const birthDateTimestamp = moment(payload.birthDate).valueOf();
		if (!Number.isNaN(birthDateTimestamp)) {
			worker.birthDate = birthDateTimestamp;
		}
	}

	for (const field of COMMAND_WORKER_FIELDS) {
		const value = commandResponse[field];
		if (value !== undefined && value !== null) {
			// biome-ignore lint/suspicious/noExplicitAny: Init Biome
			(worker as any)[field] = value;
		}
	}

	return worker;
};
